import React from 'react';
import { AnyAction, compose } from '@reduxjs/toolkit';
import {
    apiSettingsActionCreators,
    uiActionCreators,
    connectFromCart,
    CartAppState,
    translationActionCreators,
    OrchestrationServiceInfo,
    UserSettings,
    WhiteLabelConfig,
    SessionInfo,
    ClientInfo,
    isCartApiCallReady,
    Feature,
    featuresActionCreators,
    useCartAnalyticsConfig,
    LoggerHandler,
    loggerActionCreators,
    MeasurementHandler,
} from '@cart-js/core';
import { useCartConfig } from 'core/context/WrappedCartConfig/hooks/useCartConfig';
import { useCartIngress } from 'core/context/WrappedCartConfig/hooks/useCartIngress';
import { useCartFeature } from 'core/context/WrappedCartConfig/hooks/useCartFeature';
import { mapHeaderAnalyticsProps } from 'component/common/Header/header.selectors';
import { useSelector } from 'react-redux';
import { AnalyticsActions } from 'core/messaging/analytics/AnalyticsActions';
import { selectPageTypeIdFromBootstrap } from 'store/slice/bootstrap';

export interface CartDispatchProps {
    setClientInfo: (clientInfo: ClientInfo) => void;
    setUserSettings: (userSettings: UserSettings) => void;
    setServices: (services: OrchestrationServiceInfo) => void;
    setWhiteLabelConfig: (wlConfig: WhiteLabelConfig) => void;
    setSessionInfo: (sessionInfo: SessionInfo) => void;
    fetchCms: () => void;
    setCartFeature: (features: Partial<Record<Feature, boolean>>) => void;
    setLogger: (logger: LoggerHandler) => void;
    setMeasurement: (measurement: MeasurementHandler) => void;
    setIsWebView: (isWebView: boolean) => void;
}
export interface CartStateProps {
    isCartApiReady: boolean;
}

type Props = CartStateProps & CartDispatchProps;

const WrappedCartConfigComponent: React.FC<Props> = React.memo(({ isCartApiReady, fetchCms, setCartFeature, ...setupConfigs }: Props) => {
    const pageTypeId = useSelector(selectPageTypeIdFromBootstrap);
    const { analytics } = mapHeaderAnalyticsProps(pageTypeId);
    useCartConfig(setupConfigs);
    useCartIngress({ fetchCms, isCartApiReady });
    useCartFeature({ setCartFeature });
    useCartAnalyticsConfig({
        visit: analytics.visit,
        leave: analytics.leave,
        click: (elementName: string, additionalProps?: object) => analytics.click(elementName, 1, additionalProps),
        seen: (elementName: string, additionalProps?: object) =>
            analytics.custom(AnalyticsActions.Seen, {
                ...additionalProps,
                action_element_name: elementName,
            }),
    });
    return null;
});
WrappedCartConfigComponent.displayName = 'WrappedCartConfig';

export const mapCartDispatchToProps = (dispatch: React.Dispatch<AnyAction>) => ({
    setClientInfo: (clientInfo: ClientInfo) => dispatch(apiSettingsActionCreators.setClientInfo(clientInfo)),
    setUserSettings: (userSettings: UserSettings) => dispatch(apiSettingsActionCreators.setUserSettings(userSettings)),
    setServices: (services: OrchestrationServiceInfo) => dispatch(apiSettingsActionCreators.addService(services)),
    setWhiteLabelConfig: (wlConfig: WhiteLabelConfig) => dispatch(apiSettingsActionCreators.setWhiteLabelConfig(wlConfig)),
    setSessionInfo: (sessionInfo: SessionInfo) => dispatch(apiSettingsActionCreators.setSessionInfo(sessionInfo)),
    fetchCms: () => dispatch(translationActionCreators.fetchCms() as unknown as AnyAction),
    setCartFeature: (features: Record<Feature, boolean>) => dispatch(featuresActionCreators.updateFeatures(features)),
    setLogger: (logger: LoggerHandler) => dispatch(loggerActionCreators.setLoggerHandler(logger) as unknown as AnyAction),
    setMeasurement: (measurement: MeasurementHandler) => dispatch(loggerActionCreators.setMeasurementHandler(measurement) as unknown as AnyAction),
    setIsWebView: (isWebView: boolean) => dispatch(uiActionCreators.setIsWebView(isWebView)),
});

const mapCartStateToProps = (state: CartAppState) => ({
    isCartApiReady: isCartApiCallReady(state),
});

const WrappedCartConfig = compose<React.ComponentType>(connectFromCart(mapCartStateToProps, mapCartDispatchToProps))(WrappedCartConfigComponent);

export { WrappedCartConfig as default, WrappedCartConfigComponent };
